import { Package, Layers, Grid3X3, Combine } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ProductType = 'standard' | 'variant' | 'composite' | 'composite_variant';

interface ProductTypeSelectorProps {
  selectedType: ProductType | null;
  onSelect: (type: ProductType) => void;
  disabled?: boolean;
}

const productTypes: {
  type: ProductType;
  title: string;
  description: string;
  icon: typeof Package;
  details: string[];
}[] = [
  {
    type: 'standard',
    title: 'Standard product',
    description: 'This product is a single SKU with its own inventory.',
    icon: Package,
    details: [
      'Single item with one price',
      'Track inventory for this product',
      'Best for simple products',
    ],
  },
  {
    type: 'variant',
    title: 'Variant product',
    description: 'This is a group of similar products with different attributes like size or color. Each variant is a unique SKU with its own inventory.',
    icon: Grid3X3,
    details: [
      'Multiple variations (size, color, etc.)',
      'Each variant has its own SKU',
      'Individual inventory per variant',
    ],
  },
  {
    type: 'composite',
    title: 'Composite product',
    description: 'This product is made up of specified quantities of one or more products. A composite is a single SKU but uses the inventory of the products within it.',
    icon: Layers,
    details: [
      'Made from other products',
      'Auto-deducts component inventory',
      'Great for bundles, kits, meals',
    ],
  },
  {
    type: 'composite_variant',
    title: 'Composite + Variant',
    description: 'A composite product with size or attribute variants. Each variant has its own recipe with different component quantities and its own price.',
    icon: Combine,
    details: [
      'Variants with different recipes',
      'Each size has its own quantities',
      'Independent pricing per variant',
    ],
  },
];

export function ProductTypeSelector({
  selectedType,
  onSelect,
  disabled = false,
}: ProductTypeSelectorProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-1">Inventory</h3>
        <p className="text-xs text-muted-foreground">
          The type of product you choose determines how we manage inventory and reporting.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {productTypes.map((pt) => {
          const Icon = pt.icon;
          const isSelected = selectedType === pt.type;
          
          return (
            <button
              key={pt.type}
              type="button"
              onClick={() => !disabled && onSelect(pt.type)}
              disabled={disabled}
              className={cn(
                "relative flex flex-col items-start p-4 rounded-lg border-2 text-left transition-all",
                "hover:border-primary/50 hover:bg-muted/30",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card"
              )}
            >
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary" />
              )}
              
              {/* Icon and Title */}
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  "p-2 rounded-lg",
                  isSelected ? "bg-primary/10" : "bg-muted"
                )}>
                  <Icon className={cn(
                    "h-5 w-5",
                    isSelected ? "text-primary" : "text-muted-foreground"
                  )} />
                </div>
                <h4 className="font-semibold text-sm">{pt.title}</h4>
              </div>
              
              {/* Description */}
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                {pt.description}
              </p>
              
              {/* Details list */}
              <ul className="space-y-1 mt-auto">
                {pt.details.map((detail, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className={cn(
                      "w-1 h-1 rounded-full",
                      isSelected ? "bg-primary" : "bg-muted-foreground/50"
                    )} />
                    {detail}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
}


















